import { memo, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Clock, CalendarPlus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { EXTEND_POST } from '../../services/apollo/mutations/postLifecycleMutations';
import { GET_USER_POSTS } from '../../services/apollo/queries/userQueries';
import {
  canExtendPost,
  EXTEND_WINDOW_DAYS,
  formatExpirationDate,
  getPostLifetimeDays,
  POST_TYPES,
  wasRecentlyExtended,
} from '../../utils/postLifecycle';


const TYPENAME_BY_POST_TYPE = Object.freeze({
  [POST_TYPES.LOST]: 'LostAnimal',
  [POST_TYPES.FOUND]: 'FoundAnimal',
  [POST_TYPES.PSA]: 'PSA',
  [POST_TYPES.PET_SERVICE]: 'PetService',
});

function translateError(t, code) {
  switch (code) {
    case 'unauthenticated':
      return t('extend_error_unauthenticated', 'You must be signed in to extend a post.');
    case 'invalid':
      return t('extend_error_invalid', 'That post can\'t be extended right now.');
    case 'not-found':
      return t('extend_error_not_found', 'We couldn\'t find this post. It may have been deleted.');
    case 'unauthorized':
      return t('extend_error_unauthorized', 'You can only extend your own posts.');
    case 'failed':
    default:
      return t('extend_error_generic', 'Something went wrong. Please try again.');
  }
}

const SUCCESS_AUTO_CLOSE_MS = 2100;

const ExtendPostModal = ({ isOpen, onClose, post, onExtended }) => {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [errorCode, setErrorCode] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const [recentlyExtendedSnapshot, setRecentlyExtendedSnapshot] = useState(false);

  const postType = post?.postType;
  const lifetimeDays = getPostLifetimeDays(postType);

  const [extendPostMutation, { loading }] = useMutation(EXTEND_POST, {
    refetchQueries: [{ query: GET_USER_POSTS }],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      const result = data?.extendPost;
      if (!result || !result.success) return;
      const typename = TYPENAME_BY_POST_TYPE[postType];
      if (!typename || !post?._id) return;
      const cacheId = cache.identify({ __typename: typename, _id: post._id });
      if (!cacheId) return;
      const nowIso = new Date().toISOString();
      cache.modify({
        id: cacheId,
        fields: {
          expiresAt: () => result.newExpiresAt || nowIso,
          extensionCount: () => result.extensionCount ?? 0,
          lastExtendedAt: () => nowIso,
        },
      });
    },
  });

  useEffect(() => {
    if (isOpen) {
      setErrorCode(null);
      setSuccessInfo(null);
      setRecentlyExtendedSnapshot(wasRecentlyExtended(post));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, post?._id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading && !successInfo) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, successInfo, onClose]);

  useEffect(() => {
    if (!successInfo) return undefined;
    const timer = setTimeout(() => {
      if (onExtended) {
        onExtended({
          newExpiresAt: successInfo.newExpiresAt,
          extensionCount: successInfo.extensionCount,
        });
      }
      onClose();
    }, SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [successInfo, onExtended, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const handleConfirm = useCallback(async () => {
    if (!post?._id || !postType) {
      setErrorCode('invalid');
      return;
    }

    if (!canExtendPost(post).allowed) return;
    setErrorCode(null);
    try {
      const { data } = await extendPostMutation({
        variables: { postId: post._id, postType },
      });
      const result = data?.extendPost;
      if (!result?.success) {
        setErrorCode(result?.error || 'failed');
        return;
      }
      setSuccessInfo({
        newExpiresAt: result.newExpiresAt,
        extensionCount: result.extensionCount,
      });
    } catch (err) {
      console.error('extendPost mutation threw:', err);
      setErrorCode('failed');
    }
  }, [post, postType, extendPostMutation]);

  if (!isOpen || !post) return null;

  const currentExpiration = formatExpirationDate(post.expiresAt, i18n.language);
  const displayTitle = post.title || post.animalName || t('this_post', 'this post');
  const eligibility = canExtendPost(post);
  const isEligible = eligibility.allowed;

  const newExpirationFormatted = successInfo
    ? formatExpirationDate(successInfo.newExpiresAt, i18n.language)
    : '';

  const canDismiss = !loading && !successInfo;
  const handleBackdropClick = canDismiss ? onClose : undefined;

  const bodyEnter = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -4 },
      };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm touch-none"
            onClick={handleBackdropClick}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          />

          <div
            className="fixed inset-0 z-[111] flex items-center justify-center px-4 sm:px-6"
            onClick={handleBackdropClick}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="extend-post-title"
              aria-describedby="extend-post-description"
            >
              <AnimatePresence mode="wait" initial={false}>
                {successInfo ? (
                  <motion.div
                    key="success"
                    {...bodyEnter}
                    transition={{ duration: reduceMotion ? 0.1 : 0.2 }}
                    className="p-6 text-center space-y-3"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-7 h-7 text-green-700" aria-hidden="true" />
                    </div>

                    <h3 id="extend-post-title" className="text-lg font-bold text-foreground">
                      {t('extend_post_success_title', 'Post extended')}
                    </h3>

                    {newExpirationFormatted && (
                      <p className="flex items-center justify-center gap-1.5 text-sm text-foreground">
                        <Clock className="w-4 h-4 text-green-700" aria-hidden="true" />
                        {t('extend_post_success_subtitle', 'Now expires {{date}}', {
                          date: newExpirationFormatted,
                        })}
                      </p>
                    )}

                    <p
                      id="extend-post-description"
                      className="text-xs text-muted-foreground leading-relaxed"
                    >
                      {t(
                        'extend_post_success_body',
                        "Nice — it'll keep showing up in the feed.",
                      )}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="prompt"
                    {...bodyEnter}
                    transition={{ duration: reduceMotion ? 0.1 : 0.2 }}
                    className="p-6 text-center space-y-3"
                  >
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                      <CalendarPlus className="w-7 h-7 text-primary" />
                    </div>

                    <h3 id="extend-post-title" className="text-lg font-bold text-foreground">
                      {t('extend_post_modal_title', 'Extend this post?')}
                    </h3>

                    <p id="extend-post-description" className="text-sm text-muted-foreground leading-relaxed">
                      {isEligible
                        ? t(
                            'extend_post_modal_body',
                            'Extending will add {{count}} more days before this post expires and disappears from the feed.',
                            { count: lifetimeDays },
                          )
                        : t(
                            'extend_post_modal_body_too_early',
                            "You can extend this post within {{count}} days of its expiration date \u2014 or any time after it's expired. Come back when it's closer to expiring.",
                            { count: EXTEND_WINDOW_DAYS },
                          )}
                    </p>

                    <div className="mt-3 px-3 py-2 bg-gray-50 rounded-lg space-y-1">
                      <p className="text-xs text-muted-foreground truncate">
                        "{displayTitle}"
                      </p>
                      {currentExpiration && (
                        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                          {t('extend_post_current_expiration', 'Currently expires {{date}}', {
                            date: currentExpiration,
                          })}
                        </p>
                      )}
                    </div>

                    {recentlyExtendedSnapshot && !errorCode && !loading && !successInfo && (
                      <p className="text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2 flex items-start gap-2 text-left">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>
                          {t(
                            'extend_post_recently_extended_notice',
                            'You already extended this post in the last 24 hours. You can still extend again if you\'d like.',
                          )}
                        </span>
                      </p>
                    )}

                    {errorCode && (
                      <p
                        role="alert"
                        className="text-xs text-red-700 bg-red-50 rounded-md px-3 py-2 flex items-start gap-2 text-left"
                      >
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{translateError(t, errorCode)}</span>
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {!successInfo && (
                <div className="flex border-t border-border">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 py-4 text-sm font-semibold text-foreground
                               hover:bg-gray-50 transition-colors border-r border-border
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('extend_post_cancel', 'Cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={loading || !isEligible}
                    title={
                      !isEligible
                        ? t(
                            'extend_post_disabled_tooltip',
                            'You can extend within {{count}} days of expiration, or any time after it has expired.',
                            { count: EXTEND_WINDOW_DAYS },
                          )
                        : undefined
                    }
                    className="flex-1 py-4 text-sm font-semibold text-primary
                               hover:bg-primary/5 transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed
                               flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        {t('extend_post_in_progress', 'Extending...')}
                      </>
                    ) : (
                      t('extend_post_confirm', 'Extend')
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default memo(ExtendPostModal);
